import { createFileRoute, redirect } from '@tanstack/react-router';
import { sessionMe } from '@/api/resources';
import { copilotEnabled, requireSession } from '@/lib/route-guards';
import { landingFor } from '@/lib/landing';
import { PHONE_QUERY } from '@/lib/responsive';
import type { User } from '@/api/types.gen';

// '/' resolves in beforeLoad so the wrong page never flashes: phones land on
// Campaigns (see `landingFor`); wider layouts go to the Daily Briefing when
// Copilot is enabled, else the first Backend nav item. The copilot probe is
// skipped on phones — it has a 1.5 s timeout and the answer would not be used.
//
// This is NOT a synchronous redirect, so it does not skip the pending skeleton.
// `beforeLoad` awaits `requireSession` and, off phones, the copilot probe —
// up to 1.5 s — before it throws, and the router arms the pending timer for a
// route with a `beforeLoad` exactly as for one with a loader (router-core
// `setupPendingTimeout`; measured on 1.171.6, 2026-09-13) — before `beforeLoad`
// is even invoked. So a redirect that lands within `defaultPendingMs`
// (router.ts) shows nothing, and past it the skeleton shows here while the
// progress bar runs, like any loader.
// That is acceptable: the wait is real, and a blank page or the wrong landing
// page would be worse. Do not add `pendingMs` here — the skeleton is the
// honest acknowledgement of a wait this long.
export const Route = createFileRoute('/')({
  // Route-data exemption: a redirect. It resolves the session and Copilot flag in `beforeLoad`
  // and renders no screen of its own (the skeleton it can show is the router's).
  beforeLoad: async (opts) => {
    await requireSession(opts);
    const phone = typeof window !== 'undefined' && !!window.matchMedia?.(PHONE_QUERY).matches;
    const user = opts.context.queryClient.getQueryData<User>(sessionMe.key(undefined));
    const copilot = phone ? false : await copilotEnabled(opts);
    throw redirect({ to: landingFor({ user, phone, copilot }) });
  },
});
